import { LDate } from '@app/components/date/LDate';
import { LDateTime } from '@app/components/date/LDateTime';
import { ScheduledAodRunDto } from '@app/modules/audit/modules/aod/aod.types';
import { toDateTimeStr } from '@mi-front/core';
import ManageHistoryIcon from '@mui/icons-material/ManageHistory';
import { IconButton, Tooltip } from '@mui/material';
import { MRT_ColumnDef } from 'material-react-table';
import { IntlFormatters } from 'react-intl';
import { Link } from 'react-router-dom';
import { AodScheduledRunActions } from '../../components/AodScheduledRunActions';
import { AodTriggerType } from '../../components/AodTriggerType';
import { aodRoutes } from '../../routing/routes';

export interface ScheduledRunsColumnsOptions {
  includeType?: boolean;
  includeActions?: boolean;
  includeName?: boolean;
  includeRuleLink?: boolean;
}

/**
 * Creates column definitions for Scheduled Runs V2.
 * Same shape as shared ScheduledRunsColumns but without enableColumnFilter, filterVariant,
 * filterFn, or enableGlobalFilter (filtering is in the ListView filter bar or client-side in queryFn).
 */
export const createScheduledRunsColumns = (
  $t: IntlFormatters['formatMessage'],
  options: ScheduledRunsColumnsOptions = {},
): MRT_ColumnDef<ScheduledAodRunDto>[] => {
  const {
    includeType = true,
    includeActions = true,
    includeName = false,
    includeRuleLink = true,
  } = options;

  const columns: MRT_ColumnDef<ScheduledAodRunDto>[] = [
    {
      id: 'scheduledAt',
      header: $t({
        id: 'audit.aod.runs.scheduledAt',
        defaultMessage: 'Programada',
      }),
      accessorFn: (row) => row.scheduledAt,
      Cell: ({ row: { original } }) => (
        <LDateTime value={toDateTimeStr(original.scheduledAt)} showRelative />
      ),
      size: 120,
      enableSorting: true,
    },
  ];

  if (includeName) {
    columns.push({
      id: 'name',
      header: $t({
        id: 'audit.aod.runs.name',
        defaultMessage: 'Nombre',
      }),
      accessorFn: (row) => row.data.name,
      Cell: ({ row: { original } }) => original.data.name || '',
      size: 150,
      enableSorting: true,
    });
  }

  if (includeType) {
    columns.push({
      id: 'type',
      header: $t({
        id: 'audit.aod.runs.type',
        defaultMessage: 'Trigger',
      }),
      accessorFn: (row) => row.data.runType,
      Cell: ({ row: { original } }) => <AodTriggerType type={original.data.runType} size="small" />,
      size: 120,
    });
  }

  columns.push({
    id: 'order',
    header: $t({
      id: 'audit.aod.runs.order',
      defaultMessage: 'Orden',
    }),
    accessorFn: (row) => row.data.order,
    Cell: ({ row: { original } }) => original.data.order,
    size: 100,
    enableSorting: true,
  });

  columns.push({
    id: 'interval',
    header: $t({
      id: 'audit.aod.runs.interval',
      defaultMessage: 'Intervalo',
    }),
    accessorFn: (row) => row.data.interval,
    Cell: ({ row: { original } }) => {
      if (!original.data.interval) return '';

      const dates = original.data.interval.split('/');
      if (dates.length !== 2) return original.data.interval;

      return (
        <div style={{ display: 'flex', gap: '0.25rem', alignItems: 'center' }}>
          <LDate value={toDateTimeStr(dates[0])} day="2-digit" month="2-digit" />
          <span>-</span>
          <LDate value={toDateTimeStr(dates[1])} day="2-digit" month="2-digit" />
        </div>
      );
    },
    size: 120,
  });

  if (includeActions) {
    columns.push({
      id: 'actions',
      header: $t({
        id: 'common.text.actions',
        defaultMessage: 'Acciones',
      }),
      accessorFn: (row) => row.jobId,
      Cell: ({ row: { original } }) => {
        const ruleId = original.data.ruleId;
        if (!ruleId) return null;
        return <AodScheduledRunActions scheduledRun={original} ruleId={ruleId} />;
      },
      size: 120,
    });
  }

  if (includeRuleLink) {
    columns.push({
      id: 'rule',
      header: $t({
        id: 'common.text.actions',
        defaultMessage: 'Acciones',
      }),
      accessorFn: (row) => row.data.ruleId,
      Cell: ({ row: { original } }) => {
        const ruleId = original.data.ruleId;
        if (!ruleId) return null;
        return (
          <Tooltip
            title={$t({
              id: 'audit.aod.runs.actions.viewRule',
              defaultMessage: 'Ir a regla',
            })}
          >
            <IconButton
              component={Link}
              to={aodRoutes.ruleView.gen(ruleId)}
              size="small"
              color="primary"
            >
              <ManageHistoryIcon fontSize="small" />
            </IconButton>
          </Tooltip>
        );
      },
      size: 80,
      enableSorting: false,
    });
  }

  return columns;
};
